import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PageHeader from "../components/PageHeader";
import { API_ENDPOINTS } from "../config/api";
import {
  LoadingState,
  ErrorState,
  EmptyState
} from "../components/CommonStates";
import PageContent from "../components/PageContent";
import { Card } from "../components/Card";
import {
  StateStats,
  StatCard,
  StatCardContent,
  StatValue,
  StatLabel,
  ComparisonText
} from "../components/Stats";
import { MapWrapper, MapBoundsFitter } from "../components/Map";
import MobileSimulatorDevice from "../components/MobileSimulatorDevice";
import { Input } from "../components/Input";
import { Dropdown } from "../components/Dropdown";
import { Form, FormGroup, Label } from "../components/Form";
import { TextArea } from "../components/Textarea";
import Button from "../components/Button";
import Messages from "./Messages";

const ContentContainer = styled.div`
  display: flex;
  height: 100%;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: 0;
`;

const RightPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-height: 0;
`;

const MapSection = styled.div`
  flex: 1;
  min-height: 400px;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: center;
  overflow-x: auto;
  box-shadow: inset 0 -0.125rem 0 0 #e1e5e9;
  position: relative;
  background: white;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  color: ${(props) => (props.$active ? "#000000" : "rgba(0, 0, 0, 0.5)")};
  white-space: nowrap;
  position: relative;

  border-bottom: 0.125rem solid transparent;
  border-bottom-color: ${(props) =>
    props.$active ? "#000000" : "transparent"};
  ${(props) => props.$active && `position: relative; z-index: 1;`}

  &:hover {
    color: rgba(0, 0, 0, 0.8);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 1rem;
    flex: 1;
    min-width: auto;
  }
`;

const TabsContainer = styled.div`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  z-index: 100;
  background: white;

  @media (max-width: 768px) {
    top: auto;
  }
`;

const LastUpdated = styled.div`
  color: #6c757d;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  text-align: left;
`;

const ChatPanel = styled.div`
  flex: 1;
  height: 100%;
  position: relative;
  overflow: auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

// Messages Wrapper Component for StatePage2
const MessagesWrapper = ({ user, selectedBot, token, onBack, onLogout }) => {
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0
      }}
    >
      <Messages
        user={user}
        selectedBot={selectedBot}
        token={token}
        onBack={onBack}
        onLogout={onLogout}
        isEmbedded={true}
      />
    </div>
  );
};

// Map states to specific bot IDs
const STATE_BOT_MAPPING = {
  pennsylvania: "pennsylvania-snap-handbook-assistant",
  mississippi: "mississippi-snap-handbook-assistant"
};

// Utility functions for calculating percentage differences from average
const calculateAverage = (allStatesData, metric) => {
  if (!allStatesData) return null;

  const validValues = allStatesData
    .filter(
      (state) =>
        state[metric] !== null &&
        state[metric] !== undefined &&
        state[metric] !== "" &&
        !isNaN(state[metric]) &&
        typeof state[metric] === "number"
    )
    .map((state) => state[metric]);

  if (validValues.length === 0) return null;

  return (
    validValues.reduce((sum, value) => sum + value, 0) / validValues.length
  );
};

const getPercentageDifferenceText = (
  allStatesData,
  currentStateData,
  metric,
  metricName,
  higherIsBetter = true
) => {
  if (
    !allStatesData ||
    !currentStateData ||
    currentStateData[metric] === null ||
    currentStateData[metric] === undefined
  ) {
    return null;
  }

  const average = calculateAverage(allStatesData, metric);
  if (average === null) return null;

  const currentValue = currentStateData[metric];
  const percentDiff = ((currentValue - average) / average) * 100;
  const absPercentDiff = Math.abs(percentDiff);

  // Handle very small differences
  if (absPercentDiff < 1) {
    return {
      text: "Near national average",
      className: "neutral",
      arrow: "→"
    };
  }

  const formattedPercent = Math.round(absPercentDiff);

  if (percentDiff > 0) {
    // Above average
    return {
      text: `${formattedPercent}% above national average`,
      className: higherIsBetter ? "positive" : "negative"
    };
  } else {
    // Below average
    return {
      text: `${formattedPercent}% below national average`,
      className: higherIsBetter ? "negative" : "positive"
    };
  }
};

const SIMULATOR_DEFAULTS = {
  type: "banner",
  bannerColor: "gold",
  bottomSheetColor: "green",
  header: "Lock your EBT card",
  subheader:
    "To prevent unauthorized use, lock your card when you're not using it. Need to buy something? Just unlock your card.",
  bannerButtonText: "Lock card now",
  bottomSheetPrimaryButtonText: "Get started",
  bottomSheetSecondaryButtonText: "Cancel",
  bottomSheetIllustration: "security",
  notificationHeader: "Title",
  notificationSubheader: "Body text",
  notificationCenterColor: "purple",
  notificationCenterIcon: "badge-percent",
  notificationCenterUnread: true,
  notificationCenterTimestamp: "2m",
  state: ""
};

function StatePage2({ user, onLogout }) {
  const { stateName } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("propel-usage");

  // State for mobile simulator
  const [type, setType] = useState(SIMULATOR_DEFAULTS.type);
  const [bannerColor, setBannerColor] = useState(
    SIMULATOR_DEFAULTS.bannerColor
  );
  const [bottomSheetColor, setBottomSheetColor] = useState(
    SIMULATOR_DEFAULTS.bottomSheetColor
  );
  const [header, setHeader] = useState(SIMULATOR_DEFAULTS.header);
  const [subheader, setSubheader] = useState(SIMULATOR_DEFAULTS.subheader);
  const [bannerButtonText, setBannerButtonText] = useState(
    SIMULATOR_DEFAULTS.bannerButtonText
  );
  const [bottomSheetPrimaryButtonText, setBottomSheetPrimaryButtonText] =
    useState(SIMULATOR_DEFAULTS.bottomSheetPrimaryButtonText);
  const [bottomSheetSecondaryButtonText, setBottomSheetSecondaryButtonText] =
    useState(SIMULATOR_DEFAULTS.bottomSheetSecondaryButtonText);
  const [bottomSheetIllustration, setBottomSheetIllustration] = useState(
    SIMULATOR_DEFAULTS.bottomSheetIllustration
  );
  const [notificationHeader, setNotificationHeader] = useState(
    SIMULATOR_DEFAULTS.notificationHeader
  );
  const [notificationSubheader, setNotificationSubheader] = useState(
    SIMULATOR_DEFAULTS.notificationSubheader
  );
  const [notificationCenterColor, setNotificationCenterColor] = useState(
    SIMULATOR_DEFAULTS.notificationCenterColor
  );
  const [notificationCenterUnread, setNotificationCenterUnread] = useState(
    SIMULATOR_DEFAULTS.notificationCenterUnread
  );
  const [notificationCenterTimestamp, setNotificationCenterTimestamp] = useState(
    SIMULATOR_DEFAULTS.notificationCenterTimestamp
  );
  const [notificationCenterIcon, setNotificationCenterIcon] = useState(
    SIMULATOR_DEFAULTS.notificationCenterIcon
  );

  // Map state name to abbreviation for simulator
  const stateToAbbr = {
    alabama: "AL",
    alaska: "AK",
    arizona: "AZ",
    arkansas: "AR",
    california: "CA",
    colorado: "CO",
    connecticut: "CT",
    delaware: "DE",
    florida: "FL",
    georgia: "GA",
    hawaii: "HI",
    idaho: "ID",
    illinois: "IL",
    indiana: "IN",
    iowa: "IA",
    kansas: "KS",
    kentucky: "KY",
    louisiana: "LA",
    maine: "ME",
    maryland: "MD",
    massachusetts: "MA",
    michigan: "MI",
    minnesota: "MN",
    mississippi: "MS",
    missouri: "MO",
    montana: "MT",
    nebraska: "NE",
    nevada: "NV",
    "new hampshire": "NH",
    "new jersey": "NJ",
    "new mexico": "NM",
    "new york": "NY",
    "north carolina": "NC",
    "north dakota": "ND",
    ohio: "OH",
    oklahoma: "OK",
    oregon: "OR",
    pennsylvania: "PA",
    "rhode island": "RI",
    "south carolina": "SC",
    "south dakota": "SD",
    tennessee: "TN",
    texas: "TX",
    utah: "UT",
    vermont: "VT",
    virginia: "VA",
    washington: "WA",
    "west virginia": "WV",
    wisconsin: "WI",
    wyoming: "WY",
    "district of columbia": "DC"
  };

  const normalizedStateName = stateName
    ? stateName.toLowerCase().replace(/-/g, " ")
    : "";
  const simulatorState = stateToAbbr[normalizedStateName] || "";

  // State for EBT theft data
  const [ebtData, setEbtData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStateData, setCurrentStateData] = useState(null);

  // Map-related state
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [ebtTheftRatesData, setEbtTheftRatesData] = useState(null);
  const [theftRatesLoading, setTheftRatesLoading] = useState(false);
  const [theftRatesError, setTheftRatesError] = useState(null);
  const [mergedData, setMergedData] = useState(null);

  // State for state summary data
  const [stateSummaryData, setStateSummaryData] = useState(null);
  const [stateSummaryLoading, setStateSummaryLoading] = useState(false);
  const [stateSummaryError, setStateSummaryError] = useState(null);
  const [currentStateSummary, setCurrentStateSummary] = useState(null);

  // State for chatbot
  const [selectedBot, setSelectedBot] = useState(null);
  const [botLoading, setBotLoading] = useState(false);
  const [botError, setBotError] = useState(null);

  // Format state name with proper capitalization
  const formattedStateName = stateName
    ? stateName
        .split("-")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";

  const handleBack = () => {
    navigate("/dashboard");
  };

  // Function to fetch GeoJSON data for county boundaries
  const fetchGeoJsonData = useCallback(async () => {
    setMapLoading(true);
    setMapError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMapError("Authentication required");
        setMapLoading(false);
        return;
      }

      const response = await fetch(
        API_ENDPOINTS.stateData(stateName, "county-shapes"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGeoJsonData(data.geoJson);
        } else {
          setMapError("Failed to load county boundaries");
        }
      } else {
        setMapError("Failed to load county boundaries from server");
      }
    } catch (error) {
      console.error("Error fetching GeoJSON data:", error);
      setMapError("Failed to load county boundaries");
    } finally {
      setMapLoading(false);
    }
  }, [stateName]);

  // Function to fetch EBT theft rates per county
  const fetchEbtTheftRates = useCallback(async () => {
    setTheftRatesLoading(true);
    setTheftRatesError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setTheftRatesError("Authentication required");
        setTheftRatesLoading(false);
        return;
      }

      const response = await fetch(
        API_ENDPOINTS.stateData(stateName, "ebt-theft-rates-per-county"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEbtTheftRatesData(data);
        } else {
          setTheftRatesError("Failed to load EBT theft rates data");
        }
      } else {
        setTheftRatesError("Failed to load EBT theft rates data from server");
      }
    } catch (error) {
      console.error("Error fetching EBT theft rates data:", error);
      setTheftRatesError("Failed to load EBT theft rates data");
    } finally {
      setTheftRatesLoading(false);
    }
  }, [stateName]);

  // Function to parse CSV data
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split("\n");
    const headers = lines[0].split(",");

    return lines.slice(1).map((line) => {
      const values = line.split(",");
      const obj = {};
      headers.forEach((header, index) => {
        const value = values[index];
        // Convert numeric strings to numbers
        if (header !== "STATE" && !isNaN(value)) {
          obj[header] = parseFloat(value);
        } else {
          obj[header] = value;
        }
      });
      return obj;
    });
  };

  // Function to fetch state summary data from CSV
  const fetchStateSummaryData = useCallback(async () => {
    setStateSummaryLoading(true);
    setStateSummaryError(null);

    try {
      const response = await fetch(
        "https://state-site-data.s3.us-east-2.amazonaws.com/state-summary-latest.csv"
      );

      if (response.ok) {
        const csvText = await response.text();
        const parsedData = parseCSV(csvText);
        console.log("Parsed CSV data:", parsedData.slice(0, 3));
        setStateSummaryData(parsedData);
      } else {
        setStateSummaryError("Failed to load state summary data");
      }
    } catch (error) {
      console.error("Error fetching state summary data:", error);
      setStateSummaryError("Failed to load state summary data");
    } finally {
      setStateSummaryLoading(false);
    }
  }, []);

  // Function to merge GeoJSON and theft rates data
  const mergeMapData = useCallback(() => {
    if (!geoJsonData || !ebtTheftRatesData) {
      return;
    }

    // Create a map from FIPS code to theft rate data
    const fipsToTheftRate = ebtTheftRatesData.data?.reduce((acc, county) => {
      // The FIPS code comes as a number, convert to string with leading zeros if needed
      const fipsCode = county.FIPS
        ? county.FIPS.toString().padStart(5, "0")
        : null;

      if (fipsCode) {
        acc[fipsCode] = county;
      }
      return acc;
    }, {});

    setMergedData(fipsToTheftRate);
  }, [geoJsonData, ebtTheftRatesData]);

  // Function to get color based on theft rate (SCORE is in decimal format, e.g., 0.026 = 2.6%)
  const getColor = (score) => {
    if (!score || score === 0) return "#f7f7f7";

    // Convert decimal to percentage for thresholding
    const percentageRate = score * 100;

    // Color scale from light to dark red based on theft rate percentage
    const colors = [
      "#fee5d9", // 0-1%
      "#fcbba1", // 1-2%
      "#fc9272", // 2-3%
      "#fb6a4a", // 3-4%
      "#ef3b2c", // 4-5%
      "#cb181d", // 5-6%
      "#a50f15", // 6-7%
      "#67000d" // 7%+
    ];

    const thresholds = [0, 1, 2, 3, 4, 5, 6, 7];

    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (percentageRate >= thresholds[i]) {
        return colors[i];
      }
    }

    return colors[0];
  };

  // Function to style GeoJSON features
  const styleFeature = (feature) => {
    const fipsCode = feature.properties.GEOID; // Use GEOID from GeoJSON data
    const countyData = mergedData?.[fipsCode];
    const score = countyData?.SCORE || 0; // SCORE comes as decimal (e.g., 0.026)

    // Use color based on theft rate score, or default gray if no data
    const defaultColor = "#e0e0e0"; // Light gray for no data
    const fillColor = score > 0 ? getColor(score) : defaultColor;

    return {
      fillColor: fillColor,
      weight: 1,
      opacity: 1,
      color: "#666",
      fillOpacity: 0.7
    };
  };

  // Function to handle feature interactions
  const onEachFeature = (feature, layer) => {
    const fipsCode = feature.properties.GEOID; // Use GEOID from GeoJSON data
    const countyName = feature.properties.NAME; // Use NAME from GeoJSON data
    const countyData = mergedData?.[fipsCode];

    if (countyData) {
      // Use SCORE field (decimal format) and convert to percentage for display
      const score = countyData.SCORE || 0;
      const theftRatePercent = score > 0 ? (score * 100).toFixed(2) : "0";
      const popupContent = `
        <div style="font-family: Arial, sans-serif;">
          <strong>${countyName} County</strong><br/>
          Theft Report Rate: ${theftRatePercent}%
        </div>
      `;
      layer.bindPopup(popupContent);
    } else {
      // Show basic info even if no theft rate data
      const popupContent = `
        <div style="font-family: Arial, sans-serif;">
          <strong>${countyName} County</strong><br/>
        </div>
      `;
      layer.bindPopup(popupContent);
    }
  };

  // Effect to merge data when both GeoJSON and theft rates data are available
  useEffect(() => {
    mergeMapData();
  }, [geoJsonData, ebtTheftRatesData, mergeMapData]);

  // Effect to fetch state summary data when component mounts
  useEffect(() => {
    fetchStateSummaryData();
  }, [fetchStateSummaryData]);

  // Effect to find current state summary data
  useEffect(() => {
    if (!stateSummaryData || !stateName) {
      console.log("State summary data or stateName not available:", {
        stateSummaryData: !!stateSummaryData,
        stateName
      });
      return;
    }

    // State abbreviation mapping
    const stateToAbbr = {
      alabama: "AL",
      alaska: "AK",
      arizona: "AZ",
      arkansas: "AR",
      california: "CA",
      colorado: "CO",
      connecticut: "CT",
      delaware: "DE",
      florida: "FL",
      georgia: "GA",
      hawaii: "HI",
      idaho: "ID",
      illinois: "IL",
      indiana: "IN",
      iowa: "IA",
      kansas: "KS",
      kentucky: "KY",
      louisiana: "LA",
      maine: "ME",
      maryland: "MD",
      massachusetts: "MA",
      michigan: "MI",
      minnesota: "MN",
      mississippi: "MS",
      missouri: "MO",
      montana: "MT",
      nebraska: "NE",
      nevada: "NV",
      "new hampshire": "NH",
      "new jersey": "NJ",
      "new mexico": "NM",
      "new york": "NY",
      "north carolina": "NC",
      "north dakota": "ND",
      ohio: "OH",
      oklahoma: "OK",
      oregon: "OR",
      pennsylvania: "PA",
      "rhode island": "RI",
      "south carolina": "SC",
      "south dakota": "SD",
      tennessee: "TN",
      texas: "TX",
      utah: "UT",
      vermont: "VT",
      virginia: "VA",
      washington: "WA",
      "west virginia": "WV",
      wisconsin: "WI",
      wyoming: "WY",
      "district of columbia": "DC"
    };

    const normalizedStateName = stateName.toLowerCase().replace(/-/g, " ");
    const stateAbbr = stateToAbbr[normalizedStateName];

    console.log("Looking for state:", {
      normalizedStateName,
      stateAbbr,
      availableStates: stateSummaryData.map((s) => s.STATE).slice(0, 10)
    });

    if (stateAbbr) {
      const summary = stateSummaryData.find((s) => s.STATE === stateAbbr);
      console.log("Found summary for state:", summary);
      setCurrentStateSummary(summary);
    }
  }, [stateSummaryData, stateName]);

  // Fetch EBT theft stats data
  useEffect(() => {
    const fetchEbtData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(API_ENDPOINTS.ebtTheftStats, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setEbtData(data);

        // Find data for current state using STATE_NAME with robust matching
        if (data && data.data && formattedStateName) {
          // Normalize state name for better matching
          const normalizeStateName = (name) => {
            if (!name) return "";
            return name
              .toLowerCase()
              .trim()
              .replace(/\s+/g, " ") // Replace multiple spaces with single space
              .replace(/-/g, " "); // Replace hyphens with spaces
          };

          const targetStateName = normalizeStateName(formattedStateName);

          const stateData = data.data.find((state) => {
            if (!state.STATE_NAME) return false;

            const apiStateName = normalizeStateName(state.STATE_NAME);

            // Try exact match first
            if (apiStateName === targetStateName) {
              return true;
            }

            // Try common state name variations
            const stateAbbreviations = {
              alabama: "al",
              alaska: "ak",
              arizona: "az",
              arkansas: "ar",
              california: "ca",
              colorado: "co",
              connecticut: "ct",
              delaware: "de",
              florida: "fl",
              georgia: "ga",
              hawaii: "hi",
              idaho: "id",
              illinois: "il",
              indiana: "in",
              iowa: "ia",
              kansas: "ks",
              kentucky: "ky",
              louisiana: "la",
              maine: "me",
              maryland: "md",
              massachusetts: "ma",
              michigan: "mi",
              minnesota: "mn",
              mississippi: "ms",
              missouri: "mo",
              montana: "mt",
              nebraska: "ne",
              nevada: "nv",
              "new hampshire": "nh",
              "new jersey": "nj",
              "new mexico": "nm",
              "new york": "ny",
              "north carolina": "nc",
              "north dakota": "nd",
              ohio: "oh",
              oklahoma: "ok",
              oregon: "or",
              pennsylvania: "pa",
              "rhode island": "ri",
              "south carolina": "sc",
              "south dakota": "sd",
              tennessee: "tn",
              texas: "tx",
              utah: "ut",
              vermont: "vt",
              virginia: "va",
              washington: "wa",
              "west virginia": "wv",
              wisconsin: "wi",
              wyoming: "wy",
              "district of columbia": "dc"
            };

            // Check if target is full name and API is abbreviation
            const targetAbbr = stateAbbreviations[targetStateName];
            if (targetAbbr && apiStateName === targetAbbr) {
              return true;
            }

            // Check if target is abbreviation and API is full name
            const apiAbbr = stateAbbreviations[apiStateName];
            if (apiAbbr && targetStateName === apiAbbr) {
              return true;
            }

            return false;
          });

          setCurrentStateData(stateData);
        }
      } catch (err) {
        console.error("Error fetching EBT theft stats:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (stateName) {
      fetchEbtData();
      fetchGeoJsonData(); // Fetch county shapes
      fetchEbtTheftRates(); // Fetch EBT theft rates per county
    }
  }, [formattedStateName, stateName, fetchGeoJsonData, fetchEbtTheftRates]);

  // Fetch bot data
  useEffect(() => {
    const fetchBotData = async () => {
      try {
        setBotLoading(true);
        const response = await fetch(API_ENDPOINTS.bots);
        if (response.ok) {
          const data = await response.json();
          const bots = data.bots || [];

          // Get the bot ID for this specific state
          const stateKey = stateName?.toLowerCase();
          const desiredBotId = STATE_BOT_MAPPING[stateKey];

          if (!desiredBotId) {
            setBotError(`No bot configured for ${stateName || "this state"}`);
            setBotLoading(false);
            return;
          }

          // Find the bot with the specified ID
          const bot = bots.find((b) => b.id === desiredBotId);

          if (!bot) {
            setBotError(
              `Bot "${desiredBotId}" not found for ${stateName || "this state"}`
            );
            setBotLoading(false);
            return;
          }

          setSelectedBot(bot);
        } else {
          setBotError("Failed to load bots from server");
        }
      } catch (error) {
        console.error("Error fetching bot:", error);
        setBotError("Failed to load assistant. Please try again.");
      } finally {
        setBotLoading(false);
      }
    };

    if (stateName) {
      fetchBotData();
    }
  }, [stateName]);

  // Check if leaflet CSS is loaded
  useEffect(() => {
    // Wait a bit for the CSS to be processed
    const checkCSS = () => {
      const leafletCSS = document.querySelector('link[href*="leaflet.css"]');
      const leafletStyles = document.querySelector("style[data-leaflet]");

      if (!leafletCSS && !leafletStyles) {
        // Try to inject the CSS manually if it's not found
        const style = document.createElement("style");
        style.setAttribute("data-leaflet", "true");
        style.textContent = `
          .leaflet-container {
            height: 100% !important;
            width: 100% !important;
          }
          .leaflet-control-zoom {
            display: none !important;
          }
          .leaflet-control-attribution {
            display: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    };

    // Check immediately and after a short delay
    checkCSS();
    setTimeout(checkCSS, 100);
  }, []);

  return (
    <>
      <PageHeader
        title={`${formattedStateName} × Propel`}
        showBackButton={true}
        onBack={handleBack}
      />
      <TabsContainer>
        <Tabs>
          <Tab
            $active={activeTab === "propel-usage"}
            onClick={() => setActiveTab("propel-usage")}
          >
            Propel Usage
          </Tab>
          <Tab
            $active={activeTab === "ebt-theft"}
            onClick={() => setActiveTab("ebt-theft")}
          >
            EBT Theft Tracker
          </Tab>
          <Tab
            $active={activeTab === "communications"}
            onClick={() => setActiveTab("communications")}
          >
            Client Outreach
          </Tab>
          <Tab
            $active={activeTab === "assistant"}
            onClick={() => setActiveTab("assistant")}
          >
            Policy Assistant
          </Tab>
        </Tabs>
      </TabsContainer>
      <PageContent
        $fullWidth
        style={{
          marginTop: "0",
          padding:
            activeTab === "assistant"
              ? "calc(80px + 60px + 16px) 0 0 0"
              : "calc(80px + 60px + 16px) 2rem 2rem 2rem",
          height: "100vh"
        }}
      >
        {activeTab === "ebt-theft" && (
          <ContentContainer>
            <LeftPanel>
              <Card
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden"
                }}
              >
                {loading && (
                  <LoadingState>Loading EBT theft statistics...</LoadingState>
                )}
                {error && <ErrorState>Error loading data: {error}</ErrorState>}
                {ebtData && !loading && currentStateData ? (
                  <>
                    {ebtData && (
                      <LastUpdated>
                        Last updated{" "}
                        {new Date(ebtData.lastUpdated).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                          }
                        )}
                      </LastUpdated>
                    )}
                    <StateStats
                      className="state-page-2-layout"
                      style={{ flex: "1", minHeight: 0 }}
                    >
                      <StatCard $compact>
                        <StatCardContent $compact>
                          <StatValue>
                            {Math.round(
                              currentStateData.MARKET_PENETRATION * 100
                            )}
                            %
                          </StatValue>
                          <StatLabel>SNAP households use Propel</StatLabel>
                          {(() => {
                            const comparison = getPercentageDifferenceText(
                              ebtData.data,
                              currentStateData,
                              "MARKET_PENETRATION",
                              "Market Penetration",
                              true // higher penetration is better
                            );
                            return (
                              <ComparisonText
                                className={comparison?.className || "neutral"}
                              >
                                {comparison ? (
                                  <>
                                    {comparison.arrow} {comparison.text}
                                  </>
                                ) : (
                                  "Comparison data unavailable"
                                )}
                              </ComparisonText>
                            );
                          })()}
                        </StatCardContent>
                      </StatCard>

                      <StatCard $compact>
                        <StatCardContent>
                          <StatValue color="#dc2626" size="large">
                            –$
                            {Math.round(
                              currentStateData.USD_THEFT || 0
                            ).toLocaleString()}
                          </StatValue>
                          <StatLabel>
                            {(() => {
                              const totalTheft = currentStateData.USD_THEFT;
                              const oosTheft = currentStateData.USD_THEFT_OOS;
                              if (totalTheft && oosTheft) {
                                const percentage =
                                  (oosTheft / totalTheft) * 100;
                                return `Funds stolen in the last 120 days (${Math.round(
                                  percentage
                                )}% out of state)`;
                              }
                              return "USD Theft";
                            })()}
                          </StatLabel>
                        </StatCardContent>
                      </StatCard>

                      <StatCard $compact>
                        <StatCardContent $compact>
                          <StatValue>
                            {Math.round(
                              currentStateData.FRAC_WITH_SECURITY_FEATURES * 100
                            )}
                            %
                          </StatValue>
                          <StatLabel>
                            Use security features available through Propel
                          </StatLabel>
                          {(() => {
                            const comparison = getPercentageDifferenceText(
                              ebtData.data,
                              currentStateData,
                              "FRAC_WITH_SECURITY_FEATURES",
                              "Security Features",
                              true // higher security is better
                            );
                            return (
                              <ComparisonText
                                className={comparison?.className || "neutral"}
                              >
                                {comparison ? (
                                  <>
                                    {comparison.arrow} {comparison.text}
                                  </>
                                ) : (
                                  "Comparison data unavailable"
                                )}
                              </ComparisonText>
                            );
                          })()}
                        </StatCardContent>
                      </StatCard>
                    </StateStats>
                  </>
                ) : (
                  <EmptyState>
                    No data available for {formattedStateName}
                  </EmptyState>
                )}
              </Card>
            </LeftPanel>

            <RightPanel>
              <MapSection>
                <Card style={{ height: "100%", padding: "0" }}>
                  <MapWrapper
                    height="100%"
                    $minHeight="400px"
                    $noBorder
                    $noShadow
                  >
                    {(mapLoading || theftRatesLoading) && (
                      <LoadingState>Loading map...</LoadingState>
                    )}
                    {mapError && <ErrorState>{mapError}</ErrorState>}
                    {(() => {
                      if (
                        geoJsonData &&
                        !mapLoading &&
                        !theftRatesLoading &&
                        !mapError
                      ) {
                        return (
                          <MapContainer
                            key="map-container"
                            center={[36.7783, -119.4179]} // California center
                            zoom={6}
                            scrollWheelZoom={false}
                            zoomControl={false}
                            doubleClickZoom={false}
                            style={{ height: "100%", width: "100%" }}
                          >
                            <TileLayer
                              url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                              attribution=""
                            />
                            <GeoJSON
                              data={geoJsonData}
                              style={styleFeature}
                              onEachFeature={onEachFeature}
                              key={JSON.stringify(mergedData)}
                            />
                            <MapBoundsFitter geoJsonData={geoJsonData} />
                          </MapContainer>
                        );
                      }
                      return null;
                    })()}
                  </MapWrapper>

                  {theftRatesError && (
                    <div
                      style={{
                        background: "#f8d7da",
                        color: "#721c24",
                        padding: "0.75rem",
                        borderRadius: "0.5rem",
                        marginTop: "1rem"
                      }}
                    >
                      {theftRatesError}
                    </div>
                  )}
                </Card>
              </MapSection>
            </RightPanel>
          </ContentContainer>
        )}
        {activeTab === "propel-usage" && (
          <ContentContainer>
            <LeftPanel>
              <Card
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden"
                }}
              >
                {loading && (
                  <LoadingState>Loading Propel usage data...</LoadingState>
                )}
                {error && <ErrorState>Error loading data: {error}</ErrorState>}
                {ebtData && !loading && currentStateData ? (
                  <>
                    <StateStats
                      className="propel-usage-layout"
                      style={{ flex: "1", minHeight: 0 }}
                    >
                      <StatCard>
                        <StatCardContent>
                          <StatValue size="large">
                            {currentStateData.CURRENT_MAU
                              ? (Math.round(currentStateData.CURRENT_MAU / 10) * 10).toLocaleString()
                              : "N/A"}
                          </StatValue>
                          <StatLabel>
                            Monthly active Propel users in {formattedStateName}
                          </StatLabel>
                        </StatCardContent>
                      </StatCard>

                      {currentStateSummary && !stateSummaryLoading && (
                        <>
                          <StatCard>
                            <StatCardContent>
                              <StatValue>
                                {currentStateSummary.SESSIONS
                                  ? (Math.round(currentStateSummary.SESSIONS / 10) * 10).toLocaleString()
                                  : "N/A"}
                              </StatValue>
                              <StatLabel>Total sessions</StatLabel>
                            </StatCardContent>
                          </StatCard>

                          <StatCard>
                            <StatCardContent>
                              <StatValue>
                                {Math.round(
                                  currentStateSummary.AVG_30D_SESSIONS / 10
                                ) * 10}
                              </StatValue>
                              <StatLabel>
                                Average monthly app opens per user
                              </StatLabel>
                            </StatCardContent>
                          </StatCard>
                        </>
                      )}

                      {stateSummaryLoading && (
                        <StatCard $compact>
                          <StatCardContent>
                            <StatLabel>Loading state summary data...</StatLabel>
                          </StatCardContent>
                        </StatCard>
                      )}

                      {stateSummaryError && (
                        <StatCard $compact>
                          <StatCardContent>
                            <StatLabel style={{ color: "#dc2626" }}>
                              {stateSummaryError}
                            </StatLabel>
                          </StatCardContent>
                        </StatCard>
                      )}

                      {!currentStateSummary &&
                        !stateSummaryLoading &&
                        !stateSummaryError && (
                          <StatCard $compact>
                            <StatCardContent>
                              <StatLabel style={{ color: "#6b7280" }}>
                                No state summary data available for{" "}
                                {formattedStateName}
                              </StatLabel>
                            </StatCardContent>
                          </StatCard>
                        )}
                    </StateStats>
                  </>
                ) : (
                  <EmptyState>
                    No data available for {formattedStateName}
                  </EmptyState>
                )}
              </Card>
            </LeftPanel>
          </ContentContainer>
        )}
        {activeTab === "assistant" && (
          <ContentContainer style={{ gap: "0" }}>
            <ChatPanel>
              {botLoading && <LoadingState>Loading assistant...</LoadingState>}
              {botError && <ErrorState>{botError}</ErrorState>}
              {selectedBot && !botLoading && (
                <MessagesWrapper
                  user={user}
                  selectedBot={selectedBot}
                  token={localStorage.getItem("token")}
                  onBack={handleBack}
                  onLogout={onLogout}
                />
              )}
            </ChatPanel>
          </ContentContainer>
        )}
        {activeTab === "communications" && (
          <ContentContainer style={{ gap: "2rem" }}>
            <Card
              style={{
                flex: "1",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}
            >
              <Form>
                <FormGroup>
                  <Label htmlFor="type">Type</Label>
                  <Dropdown
                    id="type"
                    name="type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="banner">Banner</option>
                    <option value="bottom-sheet">Bottom Sheet</option>
                    <option value="push-notification">Push Notification</option>
                    <option value="notification-center">Notification Center</option>
                  </Dropdown>
                </FormGroup>

                {type === "banner" && (
                  <FormGroup>
                    <Label htmlFor="banner-variant">Variant</Label>
                    <Dropdown
                      id="banner-variant"
                      name="banner-variant"
                      value={bannerColor}
                      onChange={(e) => setBannerColor(e.target.value)}
                    >
                      <option value="green">Promotional</option>
                      <option value="gold">Warning</option>
                      <option value="red">Alert</option>
                    </Dropdown>
                  </FormGroup>
                )}

                {type === "bottom-sheet" && (
                  <>
                    <FormGroup>
                      <Label htmlFor="bottom-sheet-variant">Variant</Label>
                      <Dropdown
                        id="bottom-sheet-variant"
                        name="bottom-sheet-variant"
                        value={bottomSheetColor}
                        onChange={(e) => setBottomSheetColor(e.target.value)}
                      >
                        <option value="green">Promotional</option>
                        <option value="gold">Warning</option>
                        <option value="red">Alert</option>
                        <option value="blue">Information</option>
                        <option value="purple">Security</option>
                      </Dropdown>
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="bottom-sheet-illustration">
                        Illustration
                      </Label>
                      <Dropdown
                        id="bottom-sheet-illustration"
                        name="bottom-sheet-illustration"
                        value={bottomSheetIllustration}
                        onChange={(e) =>
                          setBottomSheetIllustration(e.target.value)
                        }
                      >
                        <option value="security">Security</option>
                        <option value="money">Money</option>
                        <option value="weather">Weather</option>
                      </Dropdown>
                    </FormGroup>
                  </>
                )}

                {(type === "banner" || type === "bottom-sheet") && (
                  <>
                    <FormGroup>
                      <Label htmlFor="header">Header</Label>
                      <Input
                        id="header"
                        name="header"
                        value={header}
                        onChange={(e) => setHeader(e.target.value)}
                        placeholder="Enter header"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="subheader">Subheader</Label>
                      <TextArea
                        id="subheader"
                        name="subheader"
                        value={subheader}
                        onChange={(e) => setSubheader(e.target.value)}
                        placeholder="Enter subheader"
                        $size="small"
                      />
                    </FormGroup>

                    {type === "banner" && (
                      <FormGroup>
                        <Label htmlFor="banner-button-text">Button text</Label>
                        <Input
                          id="banner-button-text"
                          name="banner-button-text"
                          value={bannerButtonText}
                          onChange={(e) => setBannerButtonText(e.target.value)}
                          placeholder="Enter button text"
                        />
                      </FormGroup>
                    )}
                  </>
                )}

                {type === "bottom-sheet" && (
                  <>
                    <FormGroup>
                      <Label htmlFor="bottom-sheet-primary-button">
                        Primary button text
                      </Label>
                      <Input
                        id="bottom-sheet-primary-button"
                        name="bottom-sheet-primary-button"
                        value={bottomSheetPrimaryButtonText}
                        onChange={(e) =>
                          setBottomSheetPrimaryButtonText(e.target.value)
                        }
                        placeholder="Enter primary button text"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="bottom-sheet-secondary-button">
                        Secondary button text
                      </Label>
                      <Input
                        id="bottom-sheet-secondary-button"
                        name="bottom-sheet-secondary-button"
                        value={bottomSheetSecondaryButtonText}
                        onChange={(e) =>
                          setBottomSheetSecondaryButtonText(e.target.value)
                        }
                        placeholder="Enter secondary button text"
                      />
                    </FormGroup>
                  </>
                )}

                {type === "notification-center" && (
                  <>
                    <FormGroup>
                      <Label htmlFor="notification-center-color">Color</Label>
                      <Dropdown
                        id="notification-center-color"
                        name="notification-center-color"
                        value={notificationCenterColor}
                        onChange={(e) => setNotificationCenterColor(e.target.value)}
                      >
                        <option value="purple">Purple</option>
                        <option value="blue">Blue</option>
                        <option value="green">Green</option>
                        <option value="gold">Gold</option>
                        <option value="red">Red</option>
                      </Dropdown>
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="notification-center-icon">Icon</Label>
                      <Dropdown
                        id="notification-center-icon"
                        name="notification-center-icon"
                        value={notificationCenterIcon}
                        onChange={(e) => setNotificationCenterIcon(e.target.value)}
                      >
                        <option value="badge-percent">Badge Percent</option>
                        <option value="landmark">Landmark</option>
                        <option value="bell">Bell</option>
                        <option value="gift">Gift</option>
                        <option value="calendar">Calendar</option>
                        <option value="credit-card">Credit Card</option>
                        <option value="shield">Shield</option>
                        <option value="heart">Heart</option>
                        <option value="star">Star</option>
                        <option value="circle-info">Info</option>
                        <option value="triangle-exclamation">Warning</option>
                        <option value="circle-check">Check</option>
                        <option value="tag">Tag</option>
                        <option value="money-bill">Money</option>
                      </Dropdown>
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="notification-center-unread">Unread</Label>
                      <Dropdown
                        id="notification-center-unread"
                        name="notification-center-unread"
                        value={notificationCenterUnread ? "true" : "false"}
                        onChange={(e) =>
                          setNotificationCenterUnread(e.target.value === "true")
                        }
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </Dropdown>
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="notification-center-timestamp">Timestamp</Label>
                      <Dropdown
                        id="notification-center-timestamp"
                        name="notification-center-timestamp"
                        value={notificationCenterTimestamp}
                        onChange={(e) =>
                          setNotificationCenterTimestamp(e.target.value)
                        }
                      >
                        <option value="2m">2m</option>
                        <option value="4h">4h</option>
                        <option value="8d">8d</option>
                      </Dropdown>
                    </FormGroup>
                  </>
                )}

                {(type === "push-notification" || type === "notification-center") && (
                  <>
                    <FormGroup>
                      <Label htmlFor="notification-title">Title</Label>
                      <Input
                        id="notification-title"
                        name="notification-title"
                        value={notificationHeader}
                        onChange={(e) => setNotificationHeader(e.target.value)}
                        placeholder="Enter notification title"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="notification-body">Body</Label>
                      <TextArea
                        id="notification-body"
                        name="notification-body"
                        value={notificationSubheader}
                        onChange={(e) =>
                          setNotificationSubheader(e.target.value)
                        }
                        placeholder="Enter notification body"
                        $size="small"
                      />
                    </FormGroup>
                  </>
                )}
              </Form>
              <Button
                variant="primary"
                style={{
                  width: "100%"
                }}
                onClick={() => {
                  alert("This feature is not ready yet!");
                }}
              >
                Send to{" "}
                {currentStateData?.CURRENT_MAU?.toLocaleString() || "..."} users
                in {formattedStateName}
              </Button>
            </Card>
            <Card
              style={{
                flex: "2",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <div
                style={{ transform: "scale(0.9)", transformOrigin: "center" }}
              >
                <MobileSimulatorDevice
                  bannerVisible={type === "banner"}
                  bannerColor={bannerColor}
                  bannerHeader={header}
                  bannerSubheader={subheader}
                  bannerButtonText={bannerButtonText}
                  bannerState={simulatorState}
                  notificationVisible={type === "push-notification"}
                  notificationCenterVisible={type === "notification-center"}
                  notificationCenterColor={notificationCenterColor}
                  notificationCenterIcon={notificationCenterIcon}
                  notificationCenterUnread={notificationCenterUnread}
                  notificationCenterTimestamp={notificationCenterTimestamp}
                  bottomSheetVisible={type === "bottom-sheet"}
                  bottomSheetColor={bottomSheetColor}
                  bottomSheetHeader={header}
                  bottomSheetSubheader={subheader}
                  bottomSheetPrimaryButtonText={bottomSheetPrimaryButtonText}
                  bottomSheetSecondaryButtonText={
                    bottomSheetSecondaryButtonText
                  }
                  bottomSheetIllustration={bottomSheetIllustration}
                  notificationHeader={notificationHeader}
                  notificationSubheader={notificationSubheader}
                />
              </div>
            </Card>
          </ContentContainer>
        )}
      </PageContent>
    </>
  );
}

export default StatePage2;
